'use client';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import React, {
  useEffect, useId, useMemo, useState
} from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { Button } from '@/components/button/button';
import { Input } from '@/components/input/input';
import Modal from '@/components/modal/modal';
import { Heading } from '@/components/typography/heading';
import { Text } from '@/components/typography/text';
import { useGrantContractAccess } from '@/hooks/mutations/useContractAccessMutations';
import { useOrgUsersQuery } from '@/hooks/queries/useOrg';
import { ContractRole } from '@/types/dataContracts';
import { User as OrgUser } from '@/types/user';

import styles from './ManageAccessSection.module.scss';
import { extractApiErrorMessage, isDeveloperUser } from './utils';

type Mode = 'owner' | 'data_reader';

type AddPrincipalModalProps = {
  contractId: string;
  // Contract display name for the Figma title pattern
  // "Add Data Contract Owner to <Contract Name>". Optional — falls back
  // to a plain title when not provided.
  contractName?: string;
  mode: Mode;
  // User ids who already have access — excluded from the picker so the
  // granter can't pick the same person twice. Includes Admins (who
  // already have implicit ownership via the org tuple).
  existingUserIds: Set<string>;
  // Restored on Melissa's KANP-1848 review (2026-06-19) after the Figma
  // 4625 redesign appeared to drop it. Rendered as informational
  // metadata on the Add Owner modal: "Bob sees the list of constellations
  // the contract is connected to — for context only, Owners do not
  // gain access to these constellations." Hidden in Data Reader mode
  // (no propagation concern there) and when empty.
  connectedConstellationPrincipals?: string[];
  isOpen: boolean;
  onClose: () => void;
};

const schema = z.object( {
  userId: z.string().min( 1, 'Pick a user' ),
  pii: z.boolean()
} );
type FormData = z.infer<typeof schema>;

/**
 * Per-mode copy.
 *
 * Owner mode follows Figma 40003779:4625 — simplified to a single email
 * picker with no PII toggle. PII defaults to TRUE on submit (backend
 * default for Owners per KANP-1475); the UI doesn't expose it because
 * the Figma redesign decided the add flow stays minimal and PII edits
 * happen in the table afterwards (KANP-1475's "View PII" column).
 *
 * Data Reader mode keeps the PII toggle so one-step "grant Reader with
 * PII" flows still work — the Figma redesign doesn't touch this modal.
 */
const MODE_COPY: Record<Mode, {
  cta: string;
  defaultPii: boolean;
  baseTitle: string;
  subtitle: string;
  showPiiCheckbox: boolean;
}> = {
  owner: {
    cta: 'Add Owner',
    defaultPii: true,
    baseTitle: 'Add Data Contract Owner',
    subtitle: 'Give an existing Developer access to manage, update, query, and delete this contract.',
    showPiiCheckbox: false
  },
  data_reader: {
    cta: 'Add Data Reader',
    defaultPii: false,
    baseTitle: 'Add Data Reader',
    subtitle: 'Give an existing Developer access to query this contract.',
    showPiiCheckbox: true
  }
};

const matchesQuery = ( u: OrgUser, q: string ): boolean => {
  if ( !q ) return true;
  const needle = q.toLowerCase();
  return u.email.toLowerCase().includes( needle ) || u.name.toLowerCase().includes( needle );
};

export function AddPrincipalModal( {
  contractId,
  contractName,
  mode,
  existingUserIds,
  connectedConstellationPrincipals = [],
  isOpen,
  onClose
}: AddPrincipalModalProps ) {
  const copy = MODE_COPY[ mode ];
  const baseId = useId();
  // Admin_middleware /users list, fetched once and cached — the picker
  // filter is client-side per KANP-1475 spec. If perf becomes an issue at
  // >500 users we can add a /users/search?q= backend endpoint as a tiny
  // follow-up (Auth0 search already exists).
  const { data: orgUsers = [], isLoading: usersLoading } = useOrgUsersQuery();
  const grant = useGrantContractAccess( contractId );

  const [ query, setQuery ] = useState( '' );

  const { register, handleSubmit, setValue, watch, reset, formState } = useForm<FormData>( {
    resolver: zodResolver( schema ),
    defaultValues: { userId: '', pii: copy.defaultPii }
  } );
  const selectedUserId = watch( 'userId' );

  const candidates = useMemo( () => {
    // isDeveloperUser bakes in blocked + word-boundary "Developer" match
    // AND excludes Admins (they have implicit ownership via the org tuple,
    // so direct grants are redundant; the panel already badges them).
    return orgUsers
      .filter( isDeveloperUser )
      .filter( ( u ) => !existingUserIds.has( u.id ) )
      .filter( ( u ) => matchesQuery( u, query ) )
      .slice( 0, 50 );
  }, [ orgUsers, query, existingUserIds ] );

  // If the user keyboard-navigated to a candidate, then typed in the
  // search to filter that candidate out, the form state would still hold
  // the (now-invisible) userId — Enter would submit a user no longer
  // shown in the picker. Clear when the selection isn't in the visible
  // candidate set.
  useEffect( () => {
    if ( selectedUserId && !candidates.some( ( u ) => u.id === selectedUserId ) ) {
      setValue( 'userId', '', { shouldValidate: false } );
    }
  }, [ candidates, selectedUserId, setValue ] );

  const selectedUser = orgUsers.find( ( u ) => u.id === selectedUserId );

  const onSubmit = handleSubmit( async( data ) => {
    try {
      await grant.mutateAsync( {
        principal: `user:${ data.userId }`,
        permission: mode as ContractRole,
        pii: data.pii
      } );
      toast.success( `${ copy.cta }: ${ selectedUser?.name ?? data.userId }` );
      reset();
      setQuery( '' );
      onClose();
    } catch( err: unknown ) {
      // Backend rejects Member targets with 400 — extract the FastAPI
      // detail so users see the verbatim message
      // ("Members cannot be granted contract access. Promote to Developer first.")
      // instead of `callJson`'s raw envelope.
      toast.error( extractApiErrorMessage( err, 'Failed to add' ) );
    }
  } );

  const handleClose = () => {
    reset();
    setQuery( '' );
    onClose();
  };

  if ( !isOpen ) return null;

  // Stable IDs so the listbox + active descendant ARIA wiring is correct
  // across re-renders. listboxId binds the search input to the listbox
  // (`aria-controls`); optionIdFor() gives the active option's id for
  // `aria-activedescendant` so screen readers announce the highlight.
  const listboxId = `${ baseId }-listbox`;
  const optionIdFor = ( userId: string ) => `${ baseId }-opt-${ userId }`;

  const handleSearchKeyDown = ( e: React.KeyboardEvent<HTMLInputElement> ) => {
    if ( candidates.length === 0 ) return;
    const currentIdx = candidates.findIndex( ( u ) => u.id === selectedUserId );
    if ( e.key === 'ArrowDown' ) {
      e.preventDefault();
      const next = currentIdx < 0 ? 0 : Math.min( currentIdx + 1, candidates.length - 1 );
      setValue( 'userId', candidates[ next ].id, { shouldValidate: true } );
    } else if ( e.key === 'ArrowUp' ) {
      e.preventDefault();
      const prev = currentIdx < 0 ? candidates.length - 1 : Math.max( currentIdx - 1, 0 );
      setValue( 'userId', candidates[ prev ].id, { shouldValidate: true } );
    } else if ( e.key === 'Enter' && selectedUserId ) {
      // Let the form's onSubmit fire — don't preventDefault on Enter while
      // a candidate is selected. The form button will handle submission.
    }
  };

  const activeOptionId = selectedUserId ? optionIdFor( selectedUserId ) : undefined;

  return (
    // mode in the modalType so resized-size persistence doesn't leak
    // between Add Owner and Add Data Reader (they have different fields
    // and may diverge layout-wise in follow-ups).
    <Modal modalType={ `add-${ mode }-modal` } initialSize="medium">
      <form onSubmit={ onSubmit } className={ styles.modalForm }>
        <Heading as="h3" size="md">
          { contractName ? `${ copy.baseTitle } to ${ contractName }` : copy.baseTitle }
        </Heading>
        <Text>{ copy.subtitle }</Text>

        { /* Shared Input handles label+a11y wiring; combobox ARIA layered
             on via the passthrough HTML attrs. */ }
        <Input
          id={ `${ baseId }-search` }
          label="Search Developers by Email"
          type="text"
          placeholder='Ex. "username@domain.com"'
          value={ query }
          onChange={ ( e ) => setQuery( e.target.value ) }
          onKeyDown={ handleSearchKeyDown }
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-controls={ listboxId }
          // Reflect whether the listbox has any selectable options.
          // WAI-ARIA combobox spec: aria-expanded should be false when
          // the popup is empty (loading / no-results), true otherwise.
          aria-expanded={ candidates.length > 0 }
          aria-activedescendant={ activeOptionId }
        />

        { /* ARIA listbox: options must be DIRECT children of the listbox —
             no <li> wrapper. Each option is a button so it's still focusable
             via mouse without breaking the listbox shape. */ }
        <div
          className={ styles.candidateList }
          role="listbox"
          id={ listboxId }
          aria-label="Candidate developers"
        >
          { usersLoading && <div className={ styles.candidateEmpty }>Loading users…</div> }
          { !usersLoading && candidates.length === 0 && (
            <div className={ styles.candidateEmpty }>
              { query ? 'No matching Developers' : 'No Developers left to add' }
            </div>
          ) }
          { candidates.map( ( u ) => (
            <button
              key={ u.id }
              type="button"
              id={ optionIdFor( u.id ) }
              role="option"
              aria-selected={ selectedUserId === u.id }
              className={ clsx( styles.candidate, { [ styles.candidateSelected ]: selectedUserId === u.id } ) }
              data-testid={ `add-principal-candidate-${ u.id }` }
              onClick={ () => setValue( 'userId', u.id, { shouldValidate: true } ) }
            >
              <span className={ styles.candidateName }>{ u.name }</span>
              <span className={ styles.candidateEmail }>{ u.email }</span>
            </button>
          ) ) }
        </div>
        { formState.errors.userId && (
          <Text className={ styles.errorText }>{ formState.errors.userId.message }</Text>
        ) }

        { /* PII toggle: Owner mode hides this per Figma 40003779:4625
             (PII default ON stays as a backend default; UI doesn't
             expose it in the add flow). Data Reader mode keeps it so
             one-step "grant Reader with PII" still works. */ }
        { copy.showPiiCheckbox && (
          <label className={ styles.checkboxRow }>
            <input type="checkbox" { ...register( 'pii' ) } />
            <span>Allow PII access</span>
            <span className={ styles.hintText }>
              Data Readers default to no PII. Check to grant PII access.
            </span>
          </label>
        ) }

        { /* Connected-constellations info block: Owner-mode only. The
             Figma 4625 frame omits it but the original design-doc
             affordance ("Bob sees what constellations are connected, as
             info only — no propagation") is still required (Melissa
             KANP-1848 review 2026-06-19). Hidden when nothing connected. */ }
        { mode === 'owner' && connectedConstellationPrincipals.length > 0 && (
          <div className={ styles.constellationInfo } data-testid="add-principal-constellation-info">
            <Text size="sm" weight="bold">Connected constellations</Text>
            <Text size="sm" className={ styles.hintText }>
              For your information — Owners do not gain access to these
              constellations.
            </Text>
            <ul className={ styles.constellationInfoList }>
              { connectedConstellationPrincipals.map( ( principal ) => (
                <li key={ principal }>{ principal.replace( /^constellation:/, '' ) }</li>
              ) ) }
            </ul>
          </div>
        ) }

        <div className={ styles.modalActions }>
          { /* "Complete required fields to add Owner" hint per Figma
               40003779:4625 footer. Only shown when no candidate is
               selected so the button is correctly explained as disabled. */ }
          { !selectedUserId && (
            <Text size="sm" className={ styles.hintText } data-testid="add-principal-required-hint">
              { `Complete required fields to add ${ mode === 'owner' ? 'Owner' : 'Data Reader' }` }
            </Text>
          ) }
          <Button type="button" variant="secondary" onClick={ handleClose } disabled={ grant.isPending }>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={ grant.isPending || !selectedUserId }>
            { grant.isPending ? 'Adding…' : copy.cta }
          </Button>
        </div>
      </form>
    </Modal>
  );
}
